import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, Paper, IconButton, CircularProgress } from '@mui/material';
import UploadIcon from '../../assets/images/icons/upload.svg';
import FileIcon from '../../assets/images/icons/file.svg';
import DeleteIcons from '../../assets/images/icons/delete.svg';
import API from '../../api/axios';

interface FileData {
    originalName: string;
    uniqueName: string;
    absolutePath: string;
    id?: string;
}

/** State can hold uploaded metadata (FileData) or raw File when deferUpload is used */
type FileUploadItem = FileData | File;
type FileUploadState = FileUploadItem | FileUploadItem[] | null;

interface FileUploadProps {
    label: string;
    acceptedFormat: string;
    onFileSelect: (fileData: any) => void;
    error?: boolean;
    helperText?: string;
    style?: React.CSSProperties;
    maxSizeMB?: number;
    initialFile?: FileData | FileData[] | null;
    multiple?: boolean;
    /** When true, files are not uploaded on select; raw File(s) are passed to onFileSelect for upload later */
    deferUpload?: boolean;
    onFileDelete?: (file: FileUploadItem) => void;
    onDeleteClick?: (file: FileUploadItem, index: number | undefined, proceed: () => void) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({
    label,
    acceptedFormat,
    onFileSelect,
    error,
    helperText,
    style,
    maxSizeMB = 2,
    initialFile,
    multiple = false,
    deferUpload = false,
    onFileDelete,
    onDeleteClick,
}) => {
    const [fileData, setFileData] = useState<FileUploadState>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [fileRequiredError, setFileRequiredError] = useState<string | null>(null);

    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // Initialize with initial file data if in edit mode
    useEffect(() => {
        if (initialFile) {
            setFileData(Array.isArray(initialFile) ? [...initialFile] : initialFile);
        } else {
            setFileData(null);
        }
    }, [initialFile]);

    const uploadFileToServer = async (formData: FormData): Promise<any> => {
        try {
            const response = await API.post('/upload/upload_files', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files?.length) return;

        const files = Array.from(event.target.files);
        const existingData: FileUploadItem[] = fileData ? (Array.isArray(fileData) ? fileData : [fileData]) : [];

        // Security check for HTML/JS and duplicate check
        const xssPattern = /<[^>]*>|javascript:|on\w+=/i;
        const existingNames = new Set(existingData.map(item => 'originalName' in item ? item.originalName : item.name));

        for (const selectedFile of files) {
            // 1. Filename sanitization check
            if (xssPattern.test(selectedFile.name)) {
                setUploadError(`Invalid filename: HTML/JavaScript is not allowed in "${selectedFile.name}"`);
                if (fileInputRef.current) fileInputRef.current.value = "";
                return;
            }

            // 2. Duplicate check
            if (existingNames.has(selectedFile.name)) {
                setUploadError(`Duplicate file(s) are not allowed: "${selectedFile.name}" is already present.`);
                if (fileInputRef.current) fileInputRef.current.value = "";
                return;
            }

            // 3. Size check
            const fileSizeInMB = selectedFile.size / (1024 * 1024);
            if (fileSizeInMB > maxSizeMB) {
                setUploadError(`File size must be less than ${maxSizeMB}MB`);
                if (fileInputRef.current) fileInputRef.current.value = "";
                return;
            }
        }

        setUploadError(null);
        setFileRequiredError(null);

        if (deferUpload) {
            const combined = multiple ? [...existingData, ...files] : files[0];
            setFileData(combined);
            onFileSelect(combined);
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        setIsUploading(true);
        const newFilesData: FileData[] = [];
        try {
            for (const selectedFile of files) {
                const formData = new FormData();
                formData.append('file', selectedFile);
                const uploadResponse = await uploadFileToServer(formData);
                if (uploadResponse?.data?.file) {
                    newFilesData.push(uploadResponse.data.file);
                }
            }
            const combined: FileUploadState = multiple ? [...existingData, ...newFilesData] : (newFilesData[0] ?? null);
            setFileData(combined);
            onFileSelect(combined);
        } catch (error: any) {
            setUploadError(error.message || 'Error uploading file');
        } finally {
            setIsUploading(false);
        }

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const getDisplayName = (item: FileData | File) =>
        'originalName' in item ? item.originalName : item.name;

    const handleDelete = (index?: number) => {
        const itemToDelete = multiple && Array.isArray(fileData) && index !== undefined ? fileData[index] : (!Array.isArray(fileData) ? fileData : null);

        const proceed = () => {
            // Trigger parent callback if provided
            if (onFileDelete && itemToDelete) {
                onFileDelete(itemToDelete);
            }

            if (multiple && Array.isArray(fileData) && index !== undefined) {
                const updated = fileData.filter((_, i) => i !== index);
                setFileData(updated.length ? updated : null);
                onFileSelect(updated.length ? updated : null);
                setFileRequiredError(updated.length ? null : 'file is required');
            } else {
                setFileData(null);
                setUploadError(null);
                onFileSelect(null);
                setFileRequiredError('file is required');
            }

            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        };

        if (onDeleteClick && itemToDelete) {
            onDeleteClick(itemToDelete, index, proceed);
            return;
        }

        proceed();
    };

    return (
        <Box>
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                border="1px dashed #034EA2"
                padding="16px"
                borderRadius="8px"
                style={{ backgroundColor: "#F6F6F6", ...(style || {}) }}
            >
                <Box>
                    <Typography variant="body1" gutterBottom sx={{ color: '#000000', fontSize: { xs: "0.875rem", md: "1rem" } }}>
                        {label}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" marginRight={1} sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" }, minHeight: "40px" }}>
                        Accepted format: {acceptedFormat}
                    </Typography>
                    {(error || uploadError || fileRequiredError) && (
                        <Typography color="error" variant="body2">
                            {helperText || uploadError || fileRequiredError}
                        </Typography>
                    )}
                </Box>
                <Button
                    className='upload-btn'
                    variant="contained"
                    component="label"
                    color="secondary"
                    startIcon={isUploading ? (
                        <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                    ) : (
                        <img src={UploadIcon} alt="upload" />
                    )}
                    disabled={isUploading}
                >
                    {isUploading ? 'Uploading...' : 'Upload'}
                    <input
                        type="file"
                        hidden
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        multiple={multiple}
                        accept={acceptedFormat.toLowerCase().split(' or ')
                            .map(format => `.${format.trim().toLowerCase()}`)
                            .join(',')}
                    />
                </Button>
            </Box>

            {fileData && (
                <Box sx={{ 
                    maxHeight: '240px', 
                    overflowY: 'auto',
                    mt: 2,
                    pr: 0.5, // Space for scrollbar
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                        borderRadius: '10px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: '#ccc',
                        borderRadius: '10px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: '#bbb',
                    },
                }}>
                    {Array.isArray(fileData) ? (
                        fileData.map((fd: FileData | File, index: number) => (
                            <Paper
                                key={('uniqueName' in fd ? fd.uniqueName : fd.name) + index}
                                variant="outlined"
                                sx={{ mb: 2, p: 1.5, display: 'flex', alignItems: 'center', backgroundColor: '#f4f4f4', border: "none" }}
                            >
                                <Box component="img" src={FileIcon} alt="fileicon" sx={{ width: { xs: "20px", md: "25px" }, paddingX: "10px" }} />
                                <Typography variant="body2" sx={{ color: "#000000", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: "100px" }} flexGrow={1} >
                                    {getDisplayName(fd)}
                                </Typography>
                                <IconButton color="error" className="del-btn" onClick={() => handleDelete(index)}>
                                    <img src={DeleteIcons} alt="delete" />
                                </IconButton>
                            </Paper>
                        ))
                    ) : (
                        <Paper
                            variant="outlined"
                            sx={{ p: 1.5, display: 'flex', alignItems: 'center', backgroundColor: '#f4f4f4', border: "none" }}
                        >
                            <Box component="img" src={FileIcon} alt="fileicon" sx={{ width: { xs: "20px", md: "25px" }, paddingX: "10px" }} />
                            <Typography variant="body2" sx={{ color: "#000000", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: "100px" }} flexGrow={1} >
                                {getDisplayName(fileData as FileData | File)}
                            </Typography>
                            <IconButton color="error" className="del-btn" onClick={() => handleDelete()}>
                                <img src={DeleteIcons} alt="delete" />
                            </IconButton>
                        </Paper>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default FileUpload;